import { useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import Seo from '@/components/Seo';
import { MessageCircle, Send, CheckCircle2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const CATEGORIES = [
  { value: 'calculator-request', label: 'New calculator request' },
  { value: 'suggestion', label: 'Feature suggestion / tweak' },
  { value: 'bug', label: 'Something\'s broken' },
  { value: 'other', label: 'Other' },
];

export default function FeedbackPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    category: 'calculator-request',
    message: '',
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) {
      toast.error('Tell us a bit more — at least 5 characters.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/feedback', form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Could not send. Try again.');
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20">
        <div className="tm-card p-10 text-center">
          <CheckCircle2 className="mx-auto text-brand" size={56}/>
          <h1 className="font-display font-semibold text-3xl uppercase tracking-tight mt-4">Thanks for the feedback!</h1>
          <p className="text-slate-600 mt-3">Every suggestion lands in our inbox. If a new calculator gets requested often enough, we&apos;ll build it and include it free on your Pro plan.</p>
          <button onClick={() => { setSent(false); setForm({ ...form, message: '' }); }} data-testid="feedback-send-another" className="tm-btn mt-6">
            Send another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Feedback & Calculator Requests" description="Suggest a new UK trade calculator, request a feature, or report a bug. We read every message." path="/feedback" />
      <Toaster richColors position="top-right"/>
      <header className="mb-8">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Feedback</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1 flex items-center gap-3">
          <MessageCircle className="text-brand" size={36}/> What should we build next?
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Got a calculator you&apos;d find handy? A tweak to make an existing one quicker on site? Found a bug? Let us know — we read every single one and reply when we can.</p>
      </header>

      <form onSubmit={submit} className="tm-card p-6 sm:p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="tm-label">Your name</label>
            <input data-testid="feedback-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="tm-input" placeholder="Dave the Builder"/>
          </div>
          <div>
            <label className="tm-label">Email (so we can reply)</label>
            <input data-testid="feedback-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="tm-input" placeholder="you@trade.co.uk"/>
          </div>
        </div>
        <div>
          <label className="tm-label">What&apos;s this about?</label>
          <select data-testid="feedback-category" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="tm-input bg-white">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="tm-label">Your message</label>
          <textarea
            data-testid="feedback-message"
            value={form.message}
            onChange={(e) => setForm({...form, message: e.target.value})}
            rows={7}
            className="tm-input"
            placeholder="Eg: A scaffold tower hire-day calculator. Or — the brick calc should also tell me how many wall ties.
Be as detailed as you like."
            required
          />
          <p className="font-mono text-xs text-slate-500 mt-1">{form.message.length} / 4000</p>
        </div>
        <button data-testid="feedback-submit" disabled={busy} className="tm-btn">
          <Send size={16}/> {busy ? 'Sending…' : 'Send to TradesMate'}
        </button>
      </form>

      <p className="text-xs text-slate-500 uppercase tracking-widest mt-6 text-center font-semibold">
        Every Pro &amp; Pro+ subscriber gets every new calculator for free, forever.
      </p>
    </div>
  );
}
